
import React from 'react';
import { canAccessPage } from '../config/navigation';

// ─── NexoSynX N-mark (compact sidebar version) ──────────────────────────────────
function NexoSynXMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sbgrad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#2563EB" />
          <stop offset="100%" stopColor="#00D4CB" />
        </linearGradient>
      </defs>
      <path d="M10 44 L10 14 L18 14 L36 36 L36 14 L44 14 L44 44 L36 44 L18 22 L18 44 Z" fill="url(#sbgrad)" />
      <circle cx="36" cy="10" r="5" fill="#00D4CB" />
    </svg>
  );
}

// Remove acentos/diacríticos para permitir buscas sem caracteres especiais
// (ex.: "integracoes" encontra "Integrações").
function normalizeText(text) {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

export default function Sidebar({ currentPage, setCurrentPage, user, onLogout, mobileOpen = false, onMobileClose, billingOnly = false, collapsed = false, onToggleCollapse }) {
  const [expandedMenus, setExpandedMenus] = React.useState({});
  const [searchTerm, setSearchTerm] = React.useState('');
  // Recolhido só vale no desktop (lg+); o drawer mobile sempre abre completo.
  const hideIfCollapsed = collapsed ? 'lg:hidden' : '';

  // Fecha o drawer mobile ao navegar para uma tela (item simples ou submenu).
  const navigateTo = (pageId) => {
    if (billingOnly && pageId !== 'billing') return;
    setCurrentPage(pageId);
    if (onMobileClose) onMobileClose();
  };

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const menuItems = [
    {
      id: 'dashboard', label: 'Dashboard', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
    },
    {
      id: 'projects', label: 'NexoProject', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
    },
    {
      id: 'call-copilot', label: 'Copiloto de Conversas', permission: 'view_deals', isExpandable: true,
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h8M8 14h5m7-2a8 8 0 01-8 8H7l-4 2 1.5-4A8 8 0 1119 12z" /></svg>,
      submenus: [
        { id: 'call-copilot', label: 'Iniciar conversa', permission: 'view_deals' },
        { id: 'call-playbooks', label: 'Objetivos e playbooks', permission: 'view_deals' },
        { id: 'call-responses', label: 'Respostas fixas', permission: 'view_deals' },
        { id: 'call-feedback', label: 'Feedback de conversas', permission: 'view_deals' },
        { id: 'call-analytics', label: 'Analytics de conversas', permission: 'view_deals' },
      ]
    },
    {
      id: 'crm', label: 'Leads Journey', permission: 'view_leads', isExpandable: true,
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      submenus: [
        { id: 'leads',                label: 'Leads',                 permission: 'view_leads' },
        { id: 'pipeline',             label: 'Pipeline (Kanban)',      permission: 'view_leads' },
        { id: 'deals',                label: 'Negócios',               permission: 'view_dashboard' },
        { id: 'agenda',               label: 'Agenda Comercial',        permission: 'view_deals' },
        { id: 'pipeline-intelligence',label: 'Inteligência do Pipeline', permission: 'view_deals' },
        { id: 'funnels',              label: 'Config. Funis',          permission: 'view_funnels' },
      ]
    },
    {
      id: 'integrations', label: 'Integrações', permission: 'view_integrations', isExpandable: true,
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>,
      submenus: [
        { id: 'integrations', label: 'Integrações Gerais', permission: 'view_integrations' },
        { id: 'social-integrations', label: 'Redes Sociais', permission: 'view_integrations' },
        { id: 'meta-campaigns', label: 'Meta Ads', permission: 'view_integrations' },
        { id: 'tiktok-campaigns', label: 'TikTok Ads', permission: 'view_integrations' },
      ]
    },
    {
      id: 'prospecting', label: 'Prospecções', permission: 'view_prospecting', isExpandable: true,
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
      submenus: [
        { id: 'email-finder', label: 'Busca de Emails', permission: 'view_prospecting' },
        { id: 'linkedin-campaigns', label: 'LinkedIn', permission: 'view_prospecting' },
        { id: 'lead-extraction', label: 'Extração de Leads', permission: 'view_prospecting' },
      ]
    },
    {
      id: 'bulk-upload', label: 'Upload em Massa', permission: 'bulk_upload',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l3-3m0 0l3 3m-3-3v9" /></svg>
    },
    {
      id: 'unique-channel', label: 'UniqueChannel', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
    },
    {
      id: 'nexo-intelligence', label: 'SDR NexoIntelligence', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" /></svg>
    },
    {
      id: 'nexohub', label: 'NexoHub', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
    },
    {
      id: 'nexobid', label: 'NexoBid', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
    },
    {
      id: 'users', label: 'Usuários', permission: 'manage_users',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
    },
    {
      id: 'profiles', label: 'Perfis de Acesso', permission: 'manage_profiles',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
    },
    {
      id: 'companies', label: 'Empresas', permission: 'view_leads',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
    },
    {
      id: 'billing', label: 'Faturamento', permission: 'view_dashboard',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
    },
    {
      id: 'tutorial', label: 'Tutorial', permission: null,
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.168 18.477 18.582 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
    },
    {
      id: 'settings', label: 'Configurações', permission: 'manage_system',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
    },
  ];

  const hasAccess = (pageId) => billingOnly ? pageId === 'billing' : canAccessPage(pageId, user);
  const normalizedSearch = React.useMemo(() => normalizeText(searchTerm.trim()), [searchTerm]);
  const isSearching = normalizedSearch.length > 0;

  // Um menu expansível é acessível se algum submenu permitir; um item simples
  // é acessível pelo seu próprio id.
  const itemHasAccess = (item) => {
    if (item.submenus) return item.submenus.some(s => hasAccess(s.id));
    return hasAccess(item.id);
  };

  const handleNoAccess = () => {
    navigateTo(billingOnly ? 'billing' : 'dashboard');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('nexosynx:navigate', { detail: billingOnly ? 'billing' : 'dashboard' }));
    }
  };

  const isActive = (item) =>
    currentPage === item.id ||
    (item.submenus && item.submenus.some(s => currentPage === s.id));

  const initials = (user?.name || 'U').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const visibleMenuItems = React.useMemo(() => {
    if (billingOnly) return menuItems.filter(item => item.id === 'billing');
    if (!isSearching) return menuItems;

    return menuItems.reduce((accumulator, item) => {
      const parentText = normalizeText(`${item.label} ${item.id}`);
      const parentMatches = parentText.includes(normalizedSearch);

      if (!item.submenus) {
        if (parentMatches) accumulator.push(item);
        return accumulator;
      }

      const matchingSubmenus = item.submenus.filter(submenu => {
        const submenuText = normalizeText(`${submenu.label} ${submenu.id}`);
        return submenuText.includes(normalizedSearch);
      });

      if (parentMatches || matchingSubmenus.length > 0) {
        accumulator.push({
          ...item,
          visibleSubmenus: parentMatches ? item.submenus : matchingSubmenus,
        });
      }

      return accumulator;
    }, []);
  }, [billingOnly, isSearching, normalizedSearch]);

  return (
    <>
      {/* ── Overlay (apenas mobile/tablet, quando o drawer está aberto) ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(11,18,32,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 sm:w-64 ${collapsed ? 'lg:w-16' : ''} flex flex-col shadow-2xl transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'linear-gradient(180deg, #0B1220 0%, #0f1a2e 100%)', borderRight: '1px solid rgba(37,99,235,0.12)' }}
      >
        {/* ── Header / Logo ── */}
        <div
          className={`flex items-center justify-between gap-3 px-5 py-5 ${collapsed ? 'lg:px-0 lg:justify-center' : ''}`}
          style={{ borderBottom: '1px solid rgba(37,99,235,0.1)' }}
        >
          <div className="flex items-center gap-3">
            <NexoSynXMark size={34} />
            <div className={hideIfCollapsed}>
              <h1 className="text-lg leading-none" style={{ color: '#FFFFFF', letterSpacing: '-0.02em', fontFamily: "'Oxygen', sans-serif" }}>
                <span style={{ fontWeight: 400 }}>Nexo</span>
                <span style={{ fontWeight: 700 }}>SynX</span>
              </h1>
              <p className="text-xs font-semibold tracking-widest uppercase mt-0.5" style={{ color: '#00D4CB', letterSpacing: '0.12em' }}>
                CRM Completo
              </p>
            </div>
          </div>
          {/* Recolher (apenas desktop, menu expandido) */}
          {onToggleCollapse && !collapsed && (
            <button
              type="button"
              onClick={onToggleCollapse}
              title="Recolher menu"
              aria-label="Recolher menu"
              className="hidden lg:flex items-center justify-center p-2 rounded-lg transition-colors hover:bg-white/5"
              style={{ color: 'rgba(229,231,235,0.55)' }}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
          {/* Fechar (apenas mobile/tablet) */}
          <button
            onClick={onMobileClose}
            className="lg:hidden p-2 rounded-lg transition-colors"
            style={{ color: 'rgba(229,231,235,0.5)' }}
            aria-label="Fechar menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 sidebar-nav">
        {/* Expandir (apenas desktop, menu recolhido) */}
        {onToggleCollapse && collapsed && (
          <div className="hidden lg:block px-3 pb-2">
            <button
              type="button"
              onClick={onToggleCollapse}
              title={collapsed ? 'Expandir menu' : 'Recolher menu'}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors hover:bg-white/5 justify-center`}
              style={{ color: 'rgba(229,231,235,0.55)' }}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>
        )}
        <div className={`px-3 pb-3 ${hideIfCollapsed}`}>
          <div
            className="flex items-center gap-2 rounded-xl border px-3 py-2.5"
            style={{ background: 'rgba(20,35,61,0.72)', borderColor: 'rgba(37,99,235,0.15)' }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(229,231,235,0.45)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Pesquisar menus e submenus"
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-500"
              style={{ color: '#E5E7EB' }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="rounded-md p-1 transition-colors hover:bg-white/5"
                title="Limpar pesquisa"
                style={{ color: 'rgba(229,231,235,0.45)' }}
              >
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <ul className="space-y-0.5 px-3">
          {visibleMenuItems.map((item) => {
            const active = isActive(item);
            const renderedSubmenus = item.visibleSubmenus || item.submenus || [];
            const expanded = item.submenus ? (isSearching ? renderedSubmenus.length > 0 : expandedMenus[item.id]) : false;
            const allowed = itemHasAccess(item);
            return (
              <li key={item.id}>
                <button
                  data-testid={`nav-${item.id}`}
                  title={allowed ? (collapsed ? item.label : undefined) : 'Você não tem acesso a este menu'}
                  onClick={() => {
                    if (!allowed) { handleNoAccess(); return; }
                    if (item.submenus) {
                      if (!isSearching) toggleSubmenu(item.id);
                      if (!expanded || isSearching) {
                        const firstAllowed = renderedSubmenus.find(s => hasAccess(s.id));
                        if (firstAllowed) navigateTo(firstAllowed.id);
                      }
                    } else {
                      navigateTo(item.id);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all duration-150 group ${collapsed ? 'lg:justify-center' : ''}`}
                  style={{
                    background: active ? 'rgba(37,99,235,0.15)' : 'transparent',
                    borderLeft: active ? '2px solid #2563EB' : '2px solid transparent',
                    opacity: allowed ? 1 : 0.4,
                    cursor: allowed ? 'pointer' : 'not-allowed',
                  }}
                  onMouseEnter={e => { if (!active && allowed) { e.currentTarget.style.background = 'rgba(37,99,235,0.07)'; }}}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; }}}
                >
                  <div className="flex items-center gap-3">
                    <span style={{ color: active ? '#2563EB' : 'rgba(229,231,235,0.45)', transition: 'color 0.15s' }}
                      className={allowed ? 'group-hover:!text-[#00D4CB]' : ''}>
                      {item.icon}
                    </span>
                    <span
                      className={`text-sm font-medium flex items-center gap-1.5 ${hideIfCollapsed}`}
                      style={{ color: active ? '#E5E7EB' : 'rgba(229,231,235,0.55)' }}
                    >
                      {item.label}
                      {!allowed && (
                        <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(229,231,235,0.4)' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      )}
                    </span>
                  </div>
                  {item.submenus && (
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''} ${hideIfCollapsed}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      style={{ color: active ? '#2563EB' : 'rgba(229,231,235,0.3)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>

                {/* Submenus */}
                {item.submenus && expanded && (
                  <ul className={`mt-1 mb-1 ml-4 space-y-0.5 ${hideIfCollapsed}`} style={{ borderLeft: '1px solid rgba(37,99,235,0.15)', paddingLeft: '12px' }}>
                    {renderedSubmenus.map(sub => {
                        const subActive = currentPage === sub.id;
                        const subAllowed = hasAccess(sub.id);
                        return (
                          <li key={sub.id}>
                            <button
                              data-testid={`nav-${sub.id}`}
                              title={subAllowed ? undefined : 'Você não tem acesso a este menu'}
                              onClick={() => { if (subAllowed) { navigateTo(sub.id); } else { handleNoAccess(); } }}
                              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all duration-150"
                              style={{
                                background: subActive ? 'rgba(0,212,203,0.1)' : 'transparent',
                                color: subActive ? '#00D4CB' : 'rgba(229,231,235,0.45)',
                                opacity: subAllowed ? 1 : 0.4,
                                cursor: subAllowed ? 'pointer' : 'not-allowed',
                              }}
                              onMouseEnter={e => { if (!subActive && subAllowed) { e.currentTarget.style.background = 'rgba(37,99,235,0.06)'; e.currentTarget.style.color = 'rgba(229,231,235,0.75)'; }}}
                              onMouseLeave={e => { if (!subActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(229,231,235,0.45)'; }}}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ background: subActive ? '#00D4CB' : 'rgba(229,231,235,0.25)' }}
                              />
                              <span className="text-xs font-medium">{sub.label}</span>
                            </button>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </li>
            );
          })}
          {visibleMenuItems.length === 0 && (
            <li className="px-3 py-6 text-center text-xs" style={{ color: 'rgba(229,231,235,0.45)' }}>
              Nenhum menu encontrado para esta pesquisa.
            </li>
          )}
        </ul>
      </nav>

      {/* ── User Footer ── */}
      <div className="px-3 pb-4 pt-3" style={{ borderTop: '1px solid rgba(37,99,235,0.1)' }}>
        {/* User card */}
        <button
          type="button"
          data-testid="user-card-profile"
          onClick={() => navigateTo('profile')}
          className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl mb-2 transition-colors ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
          style={{ background: currentPage === 'profile' ? 'rgba(37,99,235,0.25)' : 'rgba(20,35,61,0.6)' }}
          title={collapsed ? user?.name : 'Meu perfil'}
        >
          {/* Avatar */}
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
            style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)', color: '#FFFFFF' }}
          >
            {initials}
          </div>
          <div className={`flex-1 min-w-0 ${hideIfCollapsed}`}>
            <p className="text-xs font-semibold truncate" style={{ color: '#E5E7EB' }}>{user?.name}</p>
            <p className="text-xs truncate" style={{ color: 'rgba(229,231,235,0.4)' }}>{user?.profile_name || 'Administrador'}</p>
          </div>
        </button>

        {/* Logout */}
        <button
          data-testid="logout-button"
          onClick={onLogout}
          title={collapsed ? 'Sair do sistema' : undefined}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150"
          style={{ color: 'rgba(229,231,235,0.4)', border: '1px solid rgba(37,99,235,0.12)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(229,231,235,0.4)'; e.currentTarget.style.borderColor = 'rgba(37,99,235,0.12)'; }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className={hideIfCollapsed}>Sair do sistema</span>
        </button>
      </div>
      </aside>
    </>
  );
}
